import { Market, Runner } from '@/types';
import dayjs from 'dayjs';

const fillOdds = (odds: any[], required: number) => {
  const filled = [...odds];
  while (filled.length < required) {
    filled.push({ price: 0, size: 0 });
  }
  return filled;
};

const REQUIRED_ODDS: Record<number, string[]> = {
  3: ['match odds', 'tied', 'over/under', 'half time', 'first half', 'completed match'],
  1: ['premium'],
};

// this is for inserting dummy odds button for the ui consistency
export const getRequiredOddsCount = (marketName?: string): number => {
  if (!marketName) return 1;

  const name = marketName.toLowerCase();

  for (const priority in REQUIRED_ODDS) {
    const patterns = REQUIRED_ODDS[priority];

    if (patterns?.some((p) => name.startsWith(p))) {
      return Number(priority);
    }
  }
  return 1;
};

export const getOrderedMarkets = (markets: Record<string, any[]>, marketTypes: string[]) => {
  const orderedMarkets: any[] = [];

  for (const type of marketTypes) {
    const key = Object.keys(markets).find((k) => k.toLowerCase() === type.toLowerCase());

    if (key && markets[key] && markets[key]?.length > 0) {
      orderedMarkets.push(markets[key][0]);
    }
  }

  const addedKeys = new Set(
    marketTypes.map((t) => Object.keys(markets).find((k) => k.toLowerCase() === t.toLowerCase()))
  );

  const remainingMarkets = Object.entries(markets)
    .filter(([key]) => !addedKeys.has(key))
    .sort(([aKey], [bKey]) => {
      const extractNum = (name: string) => parseFloat(name.match(/[\d.]+/)?.[0] ?? '0');
      return extractNum(aKey) - extractNum(bKey);
    })
    .map(([_, value]) => value[0])
    .filter(Boolean);

  return [...orderedMarkets, ...remainingMarkets];
};

export const formatRunners = <T extends Market>(
  marketDetails?: T,
  marketParentCategory?: string
): Runner[] => {
  if (!marketDetails || !marketDetails.runners) return [];
  const runners = Array.isArray(marketDetails.runners)
    ? [...marketDetails.runners]
    : [marketDetails.runners];

  const requiredOdds = getRequiredOddsCount(marketDetails.marketName);

  return runners
    .sort((a, b) => (a.sortPriority ?? 0) - (b.sortPriority ?? 0))
    .map((r) => {
      const back = [
        ...(r?.backPrice3 ? [{ price: r.backPrice3, size: r.backSize3 }] : []),
        ...(r?.backPrice2 ? [{ price: r.backPrice2, size: r.backSize2 }] : []),
        ...(r?.backPrice1 ? [{ price: r.backPrice1, size: r.backSize1 }] : []),
      ];

      const lay = [
        ...(r?.layPrice1 ? [{ price: r.layPrice1, size: r.laySize1 }] : []),
        ...(r?.layPrice2 ? [{ price: r.layPrice2, size: r.laySize2 }] : []),
        ...(r?.layPrice3 ? [{ price: r.layPrice3, size: r.laySize3 }] : []),
      ];

      return {
        selectionId: r.selectionId ? r.selectionId : r?.runnerId,
        // runnerId: r?.runnerId,
        sortPriority: r.sortPriority,
        runnerName: r.runnerName,
        status: r.status,
        back: fillOdds(back, requiredOdds),
        lay: marketParentCategory !== 'premium' ? fillOdds(lay, requiredOdds) : lay,
      };
    });
};

export const getFilteredMarkets = (marketObject: Record<string, any[]>) => {
  return Object.entries(marketObject).flatMap(([, markets]) => markets);
};
type DateRange = {
  fromDate?: Date | string | null;
  toDate?: Date | string | null;
};

export const resolveDateRange = (range?: DateRange, options?: { defaultDays?: number }) => {
  const defaultDays = options?.defaultDays ?? 0;

  const fromDate = range?.fromDate
    ? dayjs(range.fromDate).startOf('day')
    : defaultDays
      ? dayjs().subtract(defaultDays, 'day').startOf('day')
      : dayjs().startOf('day');

  const toDate = range?.toDate ? dayjs(range.toDate).endOf('day') : dayjs().endOf('day');

  return {
    fromDate: fromDate?.toISOString(),
    toDate: toDate?.toISOString(),
  };
};

export function maskEmail(email: string): string {
  if (!email) return '';
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  const visible = local.slice(0, 2);
  return `${visible}${'*'.repeat(Math.max(0, local?.length - 2))}@${domain}`;
}

export function maskName(name: string): string {
  if (!name) return '';
  const [first, ...rest] = name.split(' ');
  if (!first) return name;
  const maskedFirst = first[0] + '*'.repeat(Math.max(0, first?.length - 1));
  const maskedRest = rest
    .filter(Boolean)
    .map((w) => (w[0] ?? '') + '*'.repeat(Math.max(0, w?.length - 1)));
  return [maskedFirst, ...maskedRest].join(' ');
}
export function maskNickname(nickname: string): string {
  if (!nickname) return '';

  const trimmed = nickname.trim();
  if (!trimmed) return '';

  if (trimmed.length === 1) return trimmed;
  if (trimmed.length === 2) return trimmed.charAt(0) + trimmed.charAt(1);

  return trimmed.charAt(0) + '*'.repeat(trimmed.length - 2) + trimmed.charAt(trimmed.length - 1);
}
export function maskAccountNumber(value: string): string {
  if (!value) return '';
  return '*'.repeat(value?.length - 4) + value?.slice(-4);
}
export const formatActionType = (text?: string) => {
  if (!text) return '-';

  return text
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export function maskPhone(phone?: string | number): string {
  if (!phone) return '';

  const str = String(phone);

  if (str.length <= 4) return str;

  return `${str.slice(0, 2)}${'*'.repeat(str.length - 4)}${str.slice(-2)}`;
}
